import { type HTMLMotionProps, motion, type Variants } from "motion/react";
import type { ReactNode } from "react";

/* ------------------------------------------------------------------ */
/*  Shared variants                                                    */
/* ------------------------------------------------------------------ */

export const fadeBlurUp: Variants = {
	hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
	visible: { opacity: 1, y: 0, filter: "blur(0px)" },
};

export const fadeUp: Variants = {
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0 },
};

export const scaleIn: Variants = {
	hidden: { opacity: 0, scale: 0.92 },
	visible: { opacity: 1, scale: 1 },
};

/* ------------------------------------------------------------------ */
/*  Stagger container — triggers children when in viewport             */
/* ------------------------------------------------------------------ */

export function StaggerContainer({
	children,
	className,
	stagger = 0.08,
	delay = 0,
	margin = "-60px",
	once = true,
}: {
	children: ReactNode;
	className?: string;
	stagger?: number;
	delay?: number;
	margin?: string;
	once?: boolean;
}) {
	return (
		<motion.div
			className={className}
			initial="hidden"
			whileInView="visible"
			viewport={{ once, margin }}
			transition={{ staggerChildren: stagger, delayChildren: delay }}
		>
			{children}
		</motion.div>
	);
}

/* ------------------------------------------------------------------ */
/*  Single animated item                                               */
/* ------------------------------------------------------------------ */

type MotionItemProps = HTMLMotionProps<"div"> & {
	children: ReactNode;
	variants?: Variants;
};

export function MotionItem({
	children,
	variants = fadeBlurUp,
	className,
	...rest
}: MotionItemProps) {
	return (
		<motion.div
			className={className}
			variants={variants}
			transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
			{...rest}
		>
			{children}
		</motion.div>
	);
}

/* ------------------------------------------------------------------ */
/*  Viewport-triggered single element (no stagger parent needed)       */
/* ------------------------------------------------------------------ */

export function RevealOnScroll({
	children,
	className,
	variants = fadeBlurUp,
	duration = 0.65,
	delay = 0,
	margin = "-60px",
	once = true,
}: {
	children: ReactNode;
	className?: string;
	variants?: Variants;
	duration?: number;
	delay?: number;
	margin?: string;
	once?: boolean;
}) {
	return (
		<motion.div
			className={className}
			initial="hidden"
			whileInView="visible"
			viewport={{ once, margin }}
			variants={variants}
			transition={{ duration, delay, ease: [0.16, 1, 0.3, 1] }}
		>
			{children}
		</motion.div>
	);
}
